import { FC } from 'react';
import { Control } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Shield } from 'lucide-react';

interface OrderDeliveryFormData {
  dropOffWindow: {
    day: Date;
    range: string;
  };
  pickupWindow: {
    day: Date;
    range: string;
  };
  restrictions: Record<string, string>;
  placementInstructions?: string;
}

interface DeliveryDetailsTabProps {
  control: Control<OrderDeliveryFormData>;
  isGatedCommunity: boolean;
  onGatedCommunityChange: (checked: boolean) => void;
  gateCode: string;
  onGateCodeChange: (value: string) => void;
  hoaGateHours: string;
  onHoaGateHoursChange: (value: string) => void;
  requiredCredentials: string;
  onRequiredCredentialsChange: (value: string) => void;
  lockCodeAndHours: string;
  onLockCodeAndHoursChange: (value: string) => void;
  otherNotes: string;
  onOtherNotesChange: (value: string) => void;
}

export const DeliveryDetailsTab: FC<DeliveryDetailsTabProps> = ({
  control,
  isGatedCommunity,
  onGatedCommunityChange,
  gateCode,
  onGateCodeChange,
  hoaGateHours,
  onHoaGateHoursChange,
  requiredCredentials,
  onRequiredCredentialsChange,
  lockCodeAndHours,
  onLockCodeAndHoursChange,
  otherNotes,
  onOtherNotesChange,
}) => {
  return (
    <div className="space-y-6">
      {/* Access Information Header */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-1">Access Information</h4>
        <p className="text-xs text-gray-500">
          Help us ensure smooth delivery by providing access details
        </p>
      </div>

      {/* Gated Community Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mr-3">
              <Shield className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <Label htmlFor="gated-community" className="text-sm font-semibold text-gray-900">
                Gated community?
              </Label>
              <p className="text-xs text-gray-500">Let us know if we need special access</p>
            </div>
          </div>
          <Switch
            id="gated-community"
            checked={isGatedCommunity}
            onCheckedChange={onGatedCommunityChange}
            className="scale-110"
          />
        </div>

        {/* Conditional Gate Code Input */}
        {isGatedCommunity && (
          <div className="ml-0 sm:ml-13">
            <Label htmlFor="gate-code" className="text-xs font-medium text-gray-700 mb-2 block">
              Gate information or codes
            </Label>
            <Input
              id="gate-code"
              type="text"
              placeholder="Enter any gate related information"
              value={gateCode}
              onChange={(e) => onGateCodeChange(e.target.value)}
              className="h-10 border-2 border-gray-200 rounded-lg text-sm"
            />
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Additional Access Details */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-4">Additional Access Details</h4>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <Label htmlFor="hoa-hours" className="text-xs font-medium text-gray-700 mb-2 block">
              HOA gate hours
            </Label>
            <Input
              id="hoa-hours"
              type="text"
              placeholder="e.g., 6:00 AM - 10:00 PM"
              value={hoaGateHours}
              onChange={(e) => onHoaGateHoursChange(e.target.value)}
              className="h-10 border-2 border-gray-200 rounded-lg text-sm"
            />
          </div>

          <div>
            <Label htmlFor="credentials" className="text-xs font-medium text-gray-700 mb-2 block">
              Required credentials
            </Label>
            <Input
              id="credentials"
              type="text"
              placeholder="e.g., Contractor name, badge number"
              value={requiredCredentials}
              onChange={(e) => onRequiredCredentialsChange(e.target.value)}
              className="h-10 border-2 border-gray-200 rounded-lg text-sm"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="lock-code" className="text-xs font-medium text-gray-700 mb-2 block">
            Job-site lock/code and entry hours
          </Label>
          <Input
            id="lock-code"
            type="text"
            placeholder="e.g., Lock code: 1234, Access: 7:00 AM - 6:00 PM"
            value={lockCodeAndHours}
            onChange={(e) => onLockCodeAndHoursChange(e.target.value)}
            className="h-10 border-2 border-gray-200 rounded-lg text-sm"
          />
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Other Notes Section */}
      <div>
        <Label htmlFor="other-notes" className="text-sm font-semibold text-gray-900 mb-2 block">
          Other notes
        </Label>
        <p className="text-xs text-gray-500 mb-3">
          Any additional information about access, tight spaces, special instructions, etc.
        </p>
        <Textarea
          id="other-notes"
          placeholder="Tell us anything else that would help our delivery team..."
          value={otherNotes}
          onChange={(e) => onOtherNotesChange(e.target.value)}
          className="min-h-24 border-2 border-gray-200 resize-none rounded-lg text-sm placeholder:text-gray-400"
          maxLength={250}
        />
        <p className="text-xs text-gray-500 mt-2 text-right">{otherNotes.length}/250 characters</p>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Placement Instructions Section */}
      <div>
        <FormField
          control={control}
          name="placementInstructions"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-semibold text-gray-900">
                Placement Instructions (Optional)
              </FormLabel>
              <p className="text-xs text-gray-500 mb-3">
                Specific instructions for where to place the dumpster
              </p>
              <FormControl>
                <Textarea
                  {...field}
                  value={field.value || ''}
                  placeholder="e.g., Place on driveway near garage, avoid blocking mailbox"
                  className="min-h-20 border-2 border-gray-200 rounded-lg text-sm placeholder:text-gray-400"
                  maxLength={250}
                />
              </FormControl>
              <p className="text-xs text-gray-500 mt-2 text-right">
                {(field.value || '').length}/250 characters
              </p>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};
